import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Banknote, Loader2, CheckCircle, AlertTriangle, ExternalLink, RefreshCw, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Talent self-serve payout setup + status card.
 * Placed on /talent/earnings (or anywhere else).
 * Uses Stripe Connect Express hosted onboarding — we NEVER collect bank info.
 */
const PayoutsCard = () => {
  const navigate = useNavigate();
  const [loading, setLoading]         = useState(true);
  const [status, setStatus]           = useState(null);
  const [starting, setStarting]       = useState(false);
  const [dashboarding, setDashboard]  = useState(false);

  // Small, consistent "ask Evie" helper for payout/bank screens — cuts down
  // on support texts when talent hit a snag connecting their bank.
  const askEvie = (
    <button
      type="button"
      onClick={() => navigate('/talent/support?topic=payouts')}
      className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700"
      data-testid="payouts-ask-evie"
    >
      <MessageCircle className="w-3.5 h-3.5" /> Trouble connecting your bank? Ask Evie
    </button>
  );

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get('/talent/payouts/status');
      setStatus(res.data);
    } catch (e) {
      setStatus({ configured: false, error: e.response?.data?.detail || 'Status lookup failed' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const startOnboarding = async () => {
    setStarting(true);
    try {
      const origin_url = window.location.origin;
      const res = await api.post('/talent/payouts/onboarding-link', { origin_url });
      if (res.data?.onboarding_url) {
        window.location.href = res.data.onboarding_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start onboarding');
    } finally {
      setStarting(false);
    }
  };

  const openDashboard = async () => {
    // Iter-412c / P0.15 — Mobile Safari + PWA popup-blocker fix.
    //
    // The old flow was:
    //     await api.post(...)   ← async gap breaks the tap gesture
    //     window.open(url)      ← popup blocker silently returns null
    //
    // On iOS Safari (and any PWA) the tap-to-window.open chain must
    // be synchronous. We now:
    //   1. Open a blank tab IMMEDIATELY inside the tap handler.
    //   2. Kick off the API call.
    //   3. On success, redirect the pre-opened tab to Stripe.
    //   4. On failure, close it and show a real error.
    //   5. If step 1 was blocked (popup blocker returned null), fall
    //      back to full-page navigation once the URL is in hand.
    const preOpened = window.open('about:blank', '_blank');
    setDashboard(true);
    try {
      const res = await api.post('/talent/payouts/dashboard-link');
      const url = res.data?.dashboard_url;
      if (!url) throw new Error('no_url_returned');
      if (preOpened && !preOpened.closed) {
        preOpened.location.href = url;
      } else {
        // Popup blocker ate step 1 — do a same-window navigation
        // instead of silently doing nothing. Talent still lands on
        // Stripe; they can hit browser back to return.
        window.location.href = url;
      }
    } catch (e) {
      if (preOpened && !preOpened.closed) preOpened.close();
      const detail = e.response?.data?.detail;
      const msg = (detail && (detail.message || detail)) || e.message || 'Could not open Stripe dashboard';
      toast.error(typeof msg === 'string' ? msg : 'Could not open Stripe dashboard');
    } finally {
      setDashboard(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="payouts-card-loading">
        <CardContent className="py-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-cyan-500" />
        </CardContent>
      </Card>
    );
  }

  // Status 1: Not configured at all — show primary CTA
  if (!status?.configured) {
    return (
      <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50/80 to-white" data-testid="payouts-card-setup">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Banknote className="w-5 h-5 text-cyan-600" /> Direct Deposit Payouts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-3">
            Set up Stripe to receive your earnings straight to your bank account.
            Secure, hosted by Stripe — we never see your banking info.
          </p>
          <Button
            onClick={startOnboarding}
            disabled={starting}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="set-up-payouts-btn"
          >
            {starting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Banknote className="w-4 h-4 mr-1" />}
            Set Up Payouts
          </Button>
          <div>{askEvie}</div>
        </CardContent>
      </Card>
    );
  }

  // Status 2: Configured but incomplete
  if (!status.payouts_enabled || !status.details_submitted) {
    const missing = (status.requirements?.currently_due || []).length;
    return (
      <Card className="border-amber-200 bg-amber-50/60" data-testid="payouts-card-incomplete">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="w-5 h-5 text-amber-600" /> Payout Setup Incomplete
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-700 mb-2">
            Stripe needs a few more details before you can receive payouts.
            {missing > 0 && <> <span className="text-amber-700 font-semibold">{missing}</span> item{missing === 1 ? '' : 's'} still required.</>}
          </p>
          {status.requirements?.disabled_reason && (
            <p className="text-xs text-amber-700 mb-3">Reason: <code>{status.requirements.disabled_reason}</code></p>
          )}
          {status.status_error?.message && (
            <p className="text-xs text-amber-700 mb-3" data-testid="payouts-status-error">
              {status.status_error.message} <button type="button" onClick={fetchStatus} className="underline font-semibold">Retry</button>
            </p>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={startOnboarding}
              disabled={starting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="continue-payouts-btn"
            >
              {starting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-1" />}
              Continue Setup
            </Button>
            <Button variant="outline" size="sm" onClick={fetchStatus} data-testid="payouts-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          <div>{askEvie}</div>
        </CardContent>
      </Card>
    );
  }

  // Status 3: Ready — payouts enabled
  return (
    <Card className="border-emerald-200 bg-emerald-50/40" data-testid="payouts-card-ready">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <CheckCircle className="w-5 h-5 text-emerald-600" /> Payouts Enabled
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-700 mb-3">
          Your account is ready to receive direct deposits.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">payouts_enabled</Badge>
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">details_submitted</Badge>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={openDashboard}
            disabled={dashboarding}
            data-testid="open-stripe-dashboard-btn"
          >
            {dashboarding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-1" />}
            Manage in Stripe
          </Button>
          <Button variant="outline" size="sm" onClick={fetchStatus}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PayoutsCard;
